<template>
    <!-- Players -->
    <v-expansion-panels multiple v-for="v in pcEntities" v-bind:key="v.id">
        <v-expansion-panel>
            <v-expansion-panel-title>
                <v-sheet class="bg-transparent flex-grow-1" style="min-width: 0;">
                    <div class="d-flex align-center" style="gap: 8px; min-width: 0;">
                        <span v-if="!anonymizeMode" class="text-truncate">{{ v.name }}</span>
                        <span v-else-if="!getPrimaryClass(v)">[Unknown]</span>
                        <span v-if="getPrimaryClass(v)" class="text-primary font-weight-bold">[{{ getPrimaryClass(v) }}]</span>
                        <span v-if="getPrimaryWeapon(v)" class="text-success font-weight-bold">[{{ getPrimaryWeapon(v) }}]</span>
                        <span class="text-grey-lighten-1 text-caption">{{ v.guildName }}</span>
                    </div>
                    <!-- Player HP bar -->
                    <div v-if="v.maxHP > 0" class="d-flex align-center mt-1" style="gap: 6px;">
                        <v-progress-linear
                            :model-value="hpPercent(v.currentHP, v.maxHP)"
                            color="green-lighten-1"
                            bg-color="grey-darken-3"
                            height="6"
                            rounded
                            style="max-width: 160px; flex-shrink: 0;"
                        />
                        <span class="text-caption text-grey-lighten-1" style="font-size: 0.7rem; white-space: nowrap;">
                            {{ formatHP(v.currentHP) }} / {{ formatHP(v.maxHP) }}
                        </span>
                    </div>
                </v-sheet>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="pa-3">
                <v-sheet width="100%" class="mb-2">
                    h: {{ v.body.Height.toFixed(2) }} w: {{ v.body.Weight.toFixed(2) }} u: {{
                        v.body.Upper.toFixed(2) }} l: {{ v.body.Lower.toFixed(2) }}
                </v-sheet>
                <v-sheet width="100%" class="mb-2">
                    <template v-for="cond in Object.values(v.conditionMap).sort((a, b) => a.CCId - b.CCId)"
                        v-bind:key="cond.CCId">
                        <img width="16" height="16" @mouseover="e => setCondTooltip(e.target as HTMLElement, cond)"
                            @mouseleave="e => setCondTooltip(e.target as HTMLElement, undefined)"
                            @click="e => setCondTooltip(e.target as HTMLElement, cond)"
                            :src='`/res/characterconditionimage/${region}/${cond.CCId}/${cond.CCId}.png`' />
                    </template>
                </v-sheet>

                <v-sheet width="100%"
                    v-for="item in Object.values(v.equipItemMap).sort((a, b) => a.PocketType - b.PocketType)"
                    v-bind:key="item.PocketType" class="d-flex align-center mb-1 pa-1 border rounded"
                    style="background-color: rgba(0,0,0,0.03);">
                    <v-sheet width="40px" height="60px" class="mr-3 rounded overflow-hidden flex-shrink-0"
                        :style='`background: url("/res/invimage/${region}/${item.ItemId}/${item.ItemId}.png") no-repeat; background-position: center; background-size: contain;`' />
                    <v-sheet class="bg-transparent">
                        <div class="text-caption text-grey">Pocket [{{ item.PocketType }}]</div>
                        <div class="text-body-2 font-weight-bold">
                            {{ itemLabel(item) }}
                            <span class="text-caption text-grey-darken-1 font-weight-regular ml-1">({{ item.ItemId }})</span>
                        </div>
                    </v-sheet>
                </v-sheet>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>

    <!-- Enemies / tracked non-PC entities with HP data -->
    <div v-if="enemyEntities.length > 0" class="mt-4 px-2">
        <div class="text-caption text-grey mb-2" style="letter-spacing: 0.05em; text-transform: uppercase;">Tracked Enemies</div>
        <div v-for="v in enemyEntities" :key="v.id"
            class="d-flex align-center pa-2 mb-1 rounded bg-grey-darken-4"
            style="gap: 10px; min-width: 0;">
            <!-- Name -->
            <div class="text-body-2 text-truncate" style="min-width: 80px; max-width: 140px; flex-shrink: 0;">
                {{ raceNameMap[v.raceId] || v.name }}
            </div>
            <!-- HP bar -->
            <div v-if="v.maxHP > 0" class="d-flex align-center flex-grow-1" style="gap: 6px; min-width: 0;">
                <v-progress-linear
                    :model-value="hpPercent(v.currentHP, v.maxHP)"
                    :color="hpColor(v.currentHP, v.maxHP)"
                    bg-color="grey-darken-2"
                    height="10"
                    rounded
                    class="flex-grow-1"
                />
                <span class="text-caption text-grey-lighten-1" style="font-size: 0.7rem; white-space: nowrap; flex-shrink: 0;">
                    {{ formatHP(v.currentHP) }} / {{ formatHP(v.maxHP) }}
                    ({{ hpPercent(v.currentHP, v.maxHP).toFixed(1) }}%)
                </span>
            </div>
            <div v-else class="text-caption text-grey flex-grow-1">No HP data</div>
        </div>
    </div>

    <v-tooltip v-if="condTooltip" v-model="condTooltipValue" :activator="condTooltipParent">
        {{ condNameMap[condTooltip.CCId] }}
    </v-tooltip>
</template>

<script lang="ts">
import { defineComponent, inject, ref, computed, onMounted, watch, Ref } from "vue";

import { getMabiNameColor } from '@/util';
import { EntityCondition, EntityActor, ActorManager } from '@/eventActor';
import { getPrimaryClass, getPrimaryWeapon, anonymizeMode, trackedBosses } from "@/store";
import { itemName, ensureItemNames } from '@/items/itemNames';

export default defineComponent({
    setup() {
        const isLoading = inject('isLoading');
        const region = inject('region');
        const raceNameMap = inject('raceNameMap') as Ref<Record<number, string>>;
        const condNameMap = inject('condNameMap') as Ref<Record<number, string>>;
        const itemNameMap = inject('itemNameMap') as Ref<Record<number, string>>;
        const weaponNameMap = inject('weaponNameMap') as Ref<Record<number, string>>;

        // Weapons resolve through the weapon table first; everything else (and
        // unknown weapons) through the funnyresources item names.
        const itemLabel = (item: { PocketType: number; ItemId: number }) =>
            ([10, 12, 14].includes(item.PocketType) ? weaponNameMap.value?.[item.ItemId] : undefined) || itemName(item.ItemId);
        const actorManager = inject('actorManager') as Ref<ActorManager>;

        const pcEntities = computed(() =>
            Object.values(actorManager.value.entityMap)
                .filter(v => v.isPC)
                .sort((a, b) => a.name.localeCompare(b.name))
        );
        // Look up names for every item nearby players wear (cached; only new ids fetch).
        watch(() => pcEntities.value.flatMap(v => Object.values(v.equipItemMap).map((i: any) => i.ItemId as number)),
            ids => { void ensureItemNames(ids); }, { immediate: true });

        // Non-PC entities that either have HP data or have taken meaningful damage,
        // sorted by total damage taken descending so bosses appear first.
        const enemyEntities = computed(() => {
            actorManager.value.liveUpdate.value; // reactive dependency
            return Object.values(actorManager.value.entityMap)
                .filter(v => {
                    if (v.isPC) return false;
                    if (v.name.startsWith('unknown:')) return false;
                    if (!trackedBosses.value.includes(v.raceId)) return false;
                    return (v as any).maxHP > 0 || v.totalTakeDamage > 0;
                })
                .sort((a, b) => {
                    // Prioritise entities still alive (HP known > 0) and with most damage taken
                    const aAlive = (a as any).maxHP > 0 ? 1 : 0;
                    const bAlive = (b as any).maxHP > 0 ? 1 : 0;
                    if (bAlive !== aAlive) return bAlive - aAlive;
                    return b.totalTakeDamage - a.totalTakeDamage;
                })
                .slice(0, 20); // cap to avoid flooding the list
        });

        const hpPercent = (current: number, max: number) => {
            if (max <= 0) return 0;
            return Math.min(100, Math.max(0, (current / max) * 100));
        };

        const hpColor = (current: number, max: number) => {
            const pct = hpPercent(current, max);
            if (pct > 50) return 'green-lighten-1';
            if (pct > 25) return 'yellow-darken-2';
            return 'red-lighten-1';
        };

        const formatHP = (hp: number) => {
            if (hp >= 1_000_000) return `${(hp / 1_000_000).toFixed(1)}M`;
            if (hp >= 1_000) return `${(hp / 1_000).toFixed(0)}K`;
            return Math.round(hp).toString();
        };

        const condTooltipParent = ref<HTMLElement>();
        const condTooltipValue = ref(false);
        const condTooltip = ref<EntityCondition>();

        const setCondTooltip = (el: HTMLElement, cond?: EntityCondition) => {
            condTooltip.value = cond;
            condTooltipParent.value = el;
            condTooltipValue.value = !!cond;
        };

        onMounted(() => {
            console.log(pcEntities);
        });

        return {
            isLoading,
            region,

            pcEntities,
            enemyEntities,
            raceNameMap,
            condNameMap,
            itemNameMap,
            itemLabel,
            weaponNameMap,

            hpPercent,
            hpColor,
            formatHP,

            condTooltip,
            condTooltipParent,
            condTooltipValue,
            setCondTooltip,

            getMabiNameColor,
            getPrimaryClass,
            getPrimaryWeapon,
            anonymizeMode,
        };
    }
});
</script>
